import type { LotteryLiveDrawResponse as LotteryLiveDrawApiResponse } from '@shared/api-utils';
import type { IconProps } from '@shared/icons/types';
import type { CurrencyAmounts } from '@shared/utils';
import type { ComponentType } from 'react';
import type { DrawingLotteryType } from '../lotteries/utils/getLotteryDrawFrequency/getLotteryDrawFrequency';
import type { LotteryName } from '../lottery-purchases/types';

type LotteryNumberString = number | string;

type LotteryDrawResult = {
  numbers: LotteryNumberString[];
  extraNumbers?: LotteryNumberString[];
};

// Each jackpot item for a lottery
type JackpotEntry = {
  lotteryId: string;
  jackpot: CurrencyAmounts;
};

// Match condition per rank
type MatchCondition = {
  matches: { numbers: number; extraNumbers?: number };
  winners?: number;
  prize?: CurrencyAmounts;
  additionalWinners?: number;
  additionalPrize?: CurrencyAmounts;
};

// Mapping of rank number to array of match conditions
type Ranks = Record<string, MatchCondition[]>;

// Odds per lottery
type OddsEntry = {
  lotteryId: string;
  ranks?: Ranks;
};

// Extra game results (e.g., Lotto Plus 1/2)
type ExtraGame = LotteryDrawResult & {
  name: string;
};

// Additional drawing information
type AdditionalDrawingInfo = {
  originalDrawingTimes?: string[];
  winnersByRankMultiplier?: Record<string, Record<string, number>>;
};

// Main drawing structure
type Drawing = LotteryDrawResult & {
  drawingId: string;
  lotteryId: LotteryName;
  drawingDate: string;
  /** Balls in the order they were drawn. */
  drawnNumbers?: LotteryNumberString[] | null;
  /** Bonus balls in draw order. */
  drawnExtraNumbers?: LotteryNumberString[] | null;
  jackpots: JackpotEntry[];
  /** Absent while numbers are known but the prize breakdown has not published yet. */
  odds?: OddsEntry[];
  extraGames?: ExtraGame[];
  additionalDrawingInfo?: AdditionalDrawingInfo;
};

// Response is an array of drawings
type DrawingsResponse = Drawing[];

/**
 * Payload from `GET .../results/{lotteryName}/live-draw`, narrowed to the lotteries this app knows.
 *
 * The shape lives with the route in `@shared/api-utils` so the mock server and the components cannot
 * drift; only the `lotteryId` narrowing is local. See there for what the fields mean.
 */
type LotteryLiveDrawResponse = Omit<LotteryLiveDrawApiResponse, 'lotteryId'> & { lotteryId: LotteryName };

/** Inclusive integer range used to compress available years/months/days/hours */
type DrawingDateRange = {
  from: number;
  to: number;
};

/**
 * Calendar of available draw periods for archive dropdowns (LRS `getDrawingDates`).
 * Levels returned depend on lottery type: NORMAL stops at month, GO at day, HIGH_FREQUENCY at hour.
 */
type DrawingDatesResponse = {
  lotteryType: DrawingLotteryType;
  years: DrawingDateRange[];
  year: number;
  months: DrawingDateRange[];
  month: number;
  days?: DrawingDateRange[];
  day?: number;
  hours?: DrawingDateRange[];
  hour?: number;
};

/** Optional filters for `GET .../results/{lotteryName}/dates` */
type DrawingDatesQueryParams = {
  year?: number | string;
  month?: number | string;
  day?: number | string;
  timezone?: string;
};

/**
 * Query params for `GET .../results/{lotteryName}/range` (LRS `getResultsByRange`).
 * `from` is inclusive and `to` is exclusive; both are ISO-8601 UTC date-times without offset,
 * converted from the user's local selection.
 */
type ResultsByRangeQueryParams = {
  from: string;
  to: string;
  offset?: number | string;
  limit?: number | string;
};

type TableRow = {
  rank: string;
  match?: string;
  winners?: string;
  amount?: string;
};

type TableRowProps = {
  row: TableRow;
  headers: { label: string; value: keyof TableRow }[];
  index: number;
};

type ResultsConfig = {
  enableLiveDrawView?: boolean;
  /**
   * Shows Your Numbers for the selected drawing without mounting the live-draw sequence.
   * The archive date dropdown chooses that drawing; a live payload must not replace it.
   */
  showYourNumbers?: boolean;
  /**
   * Delay after the next drawing date before the results query first revalidates.
   * Polling then continues until that drawing's numbers are in the list. The offset is not a
   * stand-in for how long the results service takes to publish. Daily Million is `0`.
   */
  revalidateOffsetMs?: number;
  /**
   * How long before the next draw's closing date the results date picker includes that draw as a
   * selectable placeholder.
   */
  nextCloseLeadTime?: number;
  hideKiosk?: boolean;
  hideWinners?: boolean;
  icon?: ComponentType<IconProps>;
};

const DEFAULT_AMOUNT_DRAWS_TO_SHOW = 14;

type PrizeTableTooltip = Map<keyof TableRow, string>;

type PrizeTableTooltips = Map<number, PrizeTableTooltip>;

type TableData = {
  lotteryId: string;
  data: {
    amount: string;
    winners?: string;
    rank: string;
    match: string;
  }[];
};

export { DEFAULT_AMOUNT_DRAWS_TO_SHOW };
export type {
  AdditionalDrawingInfo,
  CurrencyAmounts,
  Drawing,
  DrawingDateRange,
  DrawingDatesQueryParams,
  DrawingDatesResponse,
  DrawingLotteryType,
  DrawingsResponse,
  ExtraGame,
  JackpotEntry,
  LotteryDrawResult,
  LotteryLiveDrawResponse,
  LotteryNumberString,
  MatchCondition,
  OddsEntry,
  PrizeTableTooltip,
  PrizeTableTooltips,
  Ranks,
  ResultsByRangeQueryParams,
  ResultsConfig,
  TableData,
  TableRow,
  TableRowProps,
};
